import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import logo from '../../assets/images/logo.jpeg';

// ─── IMPORT IMAGES FROM HOME PAGE (fitneseqp images) ─────────────────────────
import fitneseqp1 from '../../assets/images/Fitneseqp1.png';
import fitneseqp2 from '../../assets/images/fitneseqp2.png';
import fitneseqp3 from '../../assets/images/fitneseqp3.png';
import fitneseqp4 from '../../assets/images/fitneseqp4.png';
import fitneseqp5 from '../../assets/images/fitneseqp5.png';
import fitneseqp6 from '../../assets/images/fitneseqp6.png';
import fitneseqp7 from '../../assets/images/fitneseqp7.png';
import fitneseqp8 from '../../assets/images/fitneseqp8.png';
import fitneseqp9 from '../../assets/images/fitneseqp9.png';
import fitneseqp10 from '../../assets/images/fitneseqp10.png';
import fitneseqp11 from '../../assets/images/fitneseqp11.png';
import fitneseqp12 from '../../assets/images/fitneseqp12.png';

// Sauna images
import saunaPic from '../../assets/images/sauna pic.png';
import saunaPic2 from '../../assets/images/sauna pic 2.png';
import saunaPic3 from '../../assets/images/sauna pic 3.png';

// Steam image
import steamRoom from '../../assets/images/steam room.png';

// PCF Studio
import pcfStudioOut from '../../assets/images/pcf studio out.png';

// ─── TEAM MEMBER SPECIFIC IMAGES ─────────────────────────────────────────────
import muchiriImage from '../../assets/images/Muchiri 1.jpg';
import edwinImage from '../../assets/images/Coach 4.jpg';
import abou from '../../assets/images/abou.jpg';



const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

// ─── TEAM MEMBERS ─────────────────────────────────────────────────────────────
const TeamCard = ({ name, role, description, image, delay }) => {
  return (
    <motion.div
      {...fade(delay)}
      className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full"
    >
      {/* Increased image height to h-96 for even better visibility and full fit */}
      <div className="relative overflow-hidden h-96 w-full bg-gray-100">
        <img
          src={image || logo}
          alt={name}
          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4"
          style={{ background: 'linear-gradient(to top, rgba(128,0,32,0.8), transparent)' }}>
          <div className="text-white text-xs font-medium flex items-center gap-2">
            <span>✧</span> Leadership <span>✧</span>
          </div>
        </div>
      </div>
      <div className="p-5 text-center flex-1 flex flex-col justify-between">
        <div>
          <h3 className="font-black text-gray-900 text-lg mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {name}
          </h3>
          <p className="text-xs font-bold mb-2" style={{ color: '#800020' }}>{role}</p>
        </div>
        <p className="text-gray-500 text-xs leading-relaxed mt-2">{description}</p>
      </div>
    </motion.div>
  );
};

const About = () => {
  // WhatsApp number
  const WA_NUMBER = '254117110000';

  // Team members data - using specific images where provided
  const teamMembers = [
    {
      name: "Taha Khafaga",
      role: "Chief Executive Officer",
      description: "Visionary leader with a passion for fitness innovation. Taha founded PowerCore to bring world-class training facilities to Nairobi and beyond.",
      image: logo, // Will update later
    },
    {
      name: "Abou Khedr",
      role: "Director of Operations",
      description: "Operations expert dedicated to excellence. Abou ensures every PowerCore facility runs seamlessly, delivering an unmatched member experience.",
      image: abou, 
    },
    {
      name: "Muchiri",
      role: "Fitness Trainer",
      description: "Former competitive athlete turned manager, Muchiri leads our floor team with energy, expertise, and an unwavering commitment to member success.",
      image: muchiriImage,
    },
    
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
      `}</style>

      {/* ── 1. HERO - height 400px, no red overlay, clear image ── */}
      <section className="relative w-full" style={{ height: '400px' }}>
        <img src={fitneseqp1} alt="PowerCore Fitness" className="absolute inset-0 w-full h-full object-cover" />
        {/* Light dark overlay - no maroon tint, just subtle dark for text readability */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.2) 100%)' }} />
        
        <div className="absolute inset-0 flex flex-col justify-center">
          <div className="container-custom">
            <motion.div {...fade(0.1)} className="max-w-2xl">
              <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-4 border border-white/30 text-white/80">
                Our Story
              </span>
              <h1 className="font-black text-white mb-4 leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.02em' }}>
                MORE THAN A GYM.<br />
                <span style={{ color: '#800020' }}>A MOVEMENT.</span>
              </h1>
              <p className="text-white/80 text-sm leading-relaxed max-w-lg">
                Born in Nairobi. Built for champions. PowerCore exists to make world-class fitness accessible to everyone who dares to show up.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-16" style={{ background: 'linear-gradient(to top, white, transparent)' }} />
      </section>

      {/* ── 2. MISSION ── */}
      <section className="py-14 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fade(0)}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase mb-3 inline-block" style={{ color: '#800020' }}>Who We Are</span>
              <h2 className="font-black text-gray-900 mb-5 leading-tight"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
                WE DIDN'T BUILD JUST ANOTHER GYM
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                PowerCore Fitness was founded with one obsession — to create a space where ordinary people achieve extraordinary things. Nestled inside Southfield Mall along the Nairobi Express Highway, we combine luxury, science and community into one unstoppable ecosystem.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Since opening our doors in 2026, we've been redefining what fitness looks like in Nairobi — not just through premium equipment, but through an environment that respects your time, your goals and your journey.
              </p>
              <Link to="/facility"
                className="inline-flex items-center gap-2 text-sm font-bold group transition-all"
                style={{ color: '#800020' }}>
                Explore Our Facility
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div {...fade(0.15)} className="relative">
              <div className="relative rounded-3xl overflow-hidden h-80 shadow-2xl">
                <img src={fitneseqp7} alt="About PowerCore" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(128,0,32,0.2), transparent 60%)' }} />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 border border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">Established</p>
                <p className="font-black text-gray-900 text-xl" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#800020' }}>Since 2026</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 3. VALUES — minimal cards ── */}
      <section className="py-14" style={{ background: '#faf8f8' }}>
        <div className="container-custom">
          <motion.div {...fade(0)} className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>What Drives Us</span>
            <h2 className="font-black text-gray-900" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
              OUR CORE VALUES
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: '🎯', title: 'Results-Driven', desc: 'Every programme, every piece of equipment and every trainer is here for one reason — to get you results that last.' },
              { icon: '🤝', title: 'Community First', desc: 'We are a family. From your first session to your hundredth, you will always feel supported, seen and celebrated.' },
              { icon: '🌟', title: 'Uncompromising Quality', desc: 'From the shower amenities to the weight plates, we obsess over quality so you never have to think twice.' },
            ].map((v, i) => (
              <motion.div key={i} {...fade(i * 0.1)}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:border-red-100 transition-all group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: 'rgba(128,0,32,0.07)' }}>
                  {v.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{v.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. WHAT MAKES US DIFFERENT ── */}
      <section className="py-14 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fade(0.1)} className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl overflow-hidden h-48 shadow-lg col-span-2">
                  <img src={fitneseqp5} alt="Gym interior" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="rounded-2xl overflow-hidden h-36 shadow-md">
                  <img src={saunaPic} alt="Sauna facility" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="rounded-2xl overflow-hidden h-36 shadow-md">
                  <img src={steamRoom} alt="Steam room" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
            </motion.div>

            <motion.div {...fade(0)} className="order-1 lg:order-2">
              <span className="text-xs font-bold tracking-[0.2em] uppercase mb-3 inline-block" style={{ color: '#800020' }}>The PowerCore Edge</span>
              <h2 className="font-black text-gray-900 mb-6 leading-tight"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
                EVERYTHING UNDER ONE ROOF
              </h2>
              <div className="space-y-3 mb-8">
                {[
                  'State-of-the-art gym floor with 200+ equipment',
                  'Infrared sauna & eucalyptus steam room',
                  'Temperature-controlled yoga studio',
                  'Professional recovery services',
                  'Expert coaching & guidance',
                  'Access during operating hours (Mon-Fri 4AM-10PM, Sat 8AM-9PM, Sun 8AM-8PM)',
                  'Ample parking with 2hrs free at Southfield Mall',
                ].map((point, i) => (
                  <motion.div key={i} {...fade(i * 0.05)} className="flex items-start gap-3">
                    <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#800020' }} />
                    <span className="text-gray-600 text-sm">{point}</span>
                  </motion.div>
                ))}
              </div>
              <Link to="/facility"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-bold text-sm text-white transition-all hover:scale-105 group"
                style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.3)' }}>
                See The Full Facility
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 5. LEADERSHIP TEAM ── */}
      <section className="py-14 bg-white">
        <div className="container-custom">
          <motion.div {...fade(0)} className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>Our Leadership</span>
            <h2 className="font-black text-gray-900" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
              MEET THE TEAM BEHIND <span style={{ color: '#800020' }}>POWERCORE</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto mt-2">
              Visionary leaders dedicated to redefining fitness in Nairobi
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <TeamCard
                key={index}
                name={member.name}
                role={member.role}
                description={member.description}
                image={member.image}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. FACILITY BACKLINK BANNER ── */}
      <section className="py-10" style={{ background: 'linear-gradient(135deg, #f9e8ec 0%, #f0bbc6 100%)' }}>
        <div className="container-custom">
          <motion.div {...fade(0)} className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase mb-1" style={{ color: '#800020' }}>Take a Closer Look</p>
              <h3 className="font-black text-gray-900 text-2xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                CURIOUS ABOUT OUR SPACE?
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                From safety equipment to luxury finishes — every detail is designed for you.
              </p>
            </div>
            <Link to="/facility"
              className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3 rounded-full font-bold text-sm text-white transition-all hover:scale-105"
              style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.3)' }}>
              Tour the Facility <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── 7. READY TO START YOUR JOURNEY ── */}
      <section className="py-14 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #800020 0%, #3d0010 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
        </div>
        <div className="container-custom text-center relative z-10">
          <motion.div {...fade(0)}>
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
              Limited Spots Available
            </span>
            <h2 className="font-black text-white mb-4 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
              READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
            </h2>
            <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Join PowerCore today and transform your life. No contracts. No excuses. Just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
                View Pricing & Join <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <a href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Hi PowerCore! I\'d like to know more about your membership options.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default About;